import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Loader2, Gift, Check, AlertCircle } from 'lucide-react';
import { track } from './track';
import { submitPesquisa, requestPreciseGeo } from './lib/submit';

type StepKind = 'radio' | 'checkbox';

type Step = {
  id: string;
  question: string;
  kind: StepKind;
  options: string[];
};

const STEPS: Step[] = [
  {
    id: 'nivel',
    question: 'Qual é seu nível no day trade hoje?',
    kind: 'radio',
    options: [
      'Nível zero: Nunca operei.',
      'Iniciante: Já operei um pouco, mas me sinto perdido. (Menos de 30 operações)',
      'Intermediário: Vivo naquele ciclo de ganhar num dia e devolver no outro. (Entre 30 a 100 operações)',
      'Avançado: Falta "aquela virada de chave" para a consistência. (Mais de 100 operações)',
      'Já vivo do mercado: Sou consistente e busco o próximo nível.',
    ],
  },
  {
    id: 'mercados',
    question: 'Qual mercado você opera?',
    kind: 'checkbox',
    options: [
      'Nenhum, nunca operei.',
      'Índices Nacionais (Mini Indice, Mini Dolar, etc)',
      'Índices Internacionais (Dow Jones, Nasdaq, HK50, etc)',
      'Forex (Pares de moedas)',
      'Ações Nacionais',
      'Ações Internacionais',
      'Commodities (Petróleo, ouro, prata, café, etc)',
      'Criptomoedas (Moedas digitais como Bitcoin, Ethereum, etc)',
      'Opções (Não é opções binárias)',
      'Opções binárias',
    ],
  },
  {
    id: 'lives',
    question: 'Você acompanha minhas lives?',
    kind: 'radio',
    options: [
      'Nunca assisti suas lives.',
      'Já assisti algumas (1 ou 2 lives).',
      'Acompanho sempre (3 ou mais lives).',
    ],
  },
  {
    id: 'idade',
    question: 'Qual é a sua idade?',
    kind: 'radio',
    options: [
      '18 ou menos.',
      '19 a 25 anos.',
      '26 a 30 anos.',
      '31 a 35 anos.',
      '36 a 40 anos.',
      '41 a 45 anos.',
      '46 a 50 anos.',
      '51 a 55 anos.',
      '56 a 60 anos.',
      '61 a 65 anos.',
      '66 ou mais.',
    ],
  },
  {
    id: 'valoriza',
    question: 'Se você fosse adquirir um treinamento de Day Trading, o que você mais valoriza?',
    kind: 'radio',
    options: [
      'Aulas gravadas ensinando o método por completo.',
      'Sala ao vivo de operações mostrando o método na prática.',
      'Suporte humanizado.',
      'Comunidade de alunos para trocar experiências.',
    ],
  },
  {
    id: 'proximo-passo',
    question: 'Pensando no seu próximo passo após o evento Do Zero Ao Mercado Americano, qual destes caminhos faz mais sentido para você agora?',
    kind: 'radio',
    options: [
      'Absorver todo o conteúdo gratuito do evento para aplicar e seguir por conta própria.',
      'Considero seriamente investir em um treinamento pago para acelerar meus resultados.',
    ],
  },
  {
    id: 'renda',
    question: 'Qual é sua renda mensal?',
    kind: 'radio',
    options: [
      'Não tenho renda no momento.',
      'Salário mínimo de R$ 1.520,00 (ou menos).',
      'Entre R$ 1.521,00 e R$ 2.500,00 por mês.',
      'Entre R$ 2.501,00 e R$ 3.500,00 por mês.',
      'Entre R$ 3.501,00 e R$ 5.000,00 por mês.',
      'Entre R$ 5.001,00 e R$ 7.000,00 por mês.',
      'Mais de R$ 7.001,00 por mês.',
    ],
  },
];

const PesquisaPage: React.FC = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Inscrição Confirmada | Do Zero ao Mercado Americano';
  }, []);

  const step = STEPS[stepIdx];
  const total = STEPS.length;
  const progress = Math.round(((stepIdx + (submitting ? 1 : 0)) / total) * 100);
  const isLast = stepIdx === total - 1;

  const currentAnswer = answers[step.id];
  const canProceed = step.kind === 'radio'
    ? !!currentAnswer
    : Array.isArray(currentAnswer) && currentAnswer.length > 0;

  // Ao finalizar a pesquisa: dispara o prompt nativo de geolocalização do navegador.
  // O loading "Preparando seu presente" fica visível atrás enquanto o usuário decide.
  // Independente da resposta (permitir/bloquear/timeout), redireciona pro /presente.
  const finalize = async (finalAnswers: Record<string, string | string[]>) => {
    setSubmitting(true);
    track('obrigado_submit', { answers: finalAnswers });

    const geo = await requestPreciseGeo();
    track('geo_prompt_result', { granted: !!geo });

    submitPesquisa({ answers: finalAnswers, preciseGeo: geo }).catch(() => { });
    setTimeout(() => {
      window.location.href = '/presente';
    }, 1800);
  };

  const selectRadio = (option: string) => {
    setAnswers({ ...answers, [step.id]: option });
    if (error) setError(null);
    // Auto-avançar após pequena pausa para mostrar o feedback visual
    setTimeout(() => {
      track('obrigado_step', { step: step.id, index: stepIdx });
      if (isLast) {
        finalize({ ...answers, [step.id]: option });
        return;
      }
      setStepIdx(stepIdx + 1);
    }, 250);
  };

  const isExclusiveOption = (opt: string) => /^nenhum/i.test(opt);

  const toggleCheckbox = (option: string) => {
    const current = (answers[step.id] as string[] | undefined) ?? [];
    let next: string[];

    if (isExclusiveOption(option)) {
      // Clicou no "Nenhum" → seleção exclusiva + auto-avança
      if (current.includes(option)) {
        next = [];
      } else {
        next = [option];
        setAnswers({ ...answers, [step.id]: next });
        if (error) setError(null);
        setTimeout(() => {
          track('obrigado_step', { step: step.id, index: stepIdx });
          if (isLast) {
            finalize({ ...answers, [step.id]: next });
            return;
          }
          setStepIdx(stepIdx + 1);
        }, 250);
        return;
      }
    } else {
      // Clicou em uma opção normal → remove o "Nenhum" se estiver marcado
      const cleaned = current.filter(o => !isExclusiveOption(o));
      next = cleaned.includes(option)
        ? cleaned.filter(o => o !== option)
        : [...cleaned, option];
    }

    setAnswers({ ...answers, [step.id]: next });
    if (error) setError(null);
  };

  const handleBack = () => {
    if (stepIdx === 0) return;
    setError(null);
    setStepIdx(stepIdx - 1);
  };

  const handleNext = () => {
    if (!canProceed) {
      setError(
        step.kind === 'radio'
          ? 'Selecione uma opção para continuar.'
          : 'Selecione pelo menos uma opção para continuar.'
      );
      return;
    }
    setError(null);
    track('obrigado_step', { step: step.id, index: stepIdx });
    if (isLast) {
      finalize(answers);
      return;
    }
    setStepIdx(stepIdx + 1);
  };

  return (
    <div className="bg-[#131314] text-white font-sans selection:bg-flag-100 selection:text-flag-900 overflow-hidden">

      {/* Pre-header ticker — alerta */}
      <div className="w-full overflow-hidden py-2 sm:py-2.5" style={{ background: 'linear-gradient(90deg, #B31942 0%, #B31942 35%, #0A3161 65%, #0A3161 100%)' }}>
        <div className="flex animate-ticker whitespace-nowrap will-change-transform">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 sm:gap-2 text-white/90 text-[10px] sm:text-sm font-bold uppercase tracking-wider mx-3 sm:mx-4 shrink-0">
              <AlertCircle size={12} className="sm:w-3.5 sm:h-3.5" /> Não feche essa página ou você vai perder o presente exclusivo!
            </span>
          ))}
        </div>
      </div>

      {/* Main area */}
      <main className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.04)_1px,_transparent_1px)] [background-size:18px_18px] opacity-40 pointer-events-none"></div>

        {/* Ambient blurs */}
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-flag-500 opacity-[0.07] rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-navy-500 opacity-[0.08] rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-16">

          {/* Título */}
          <div className="text-center mb-7 sm:mb-8 lg:mb-10">
            <img
              src="/logo-mobile.webp"
              alt="Do Zero ao Mercado Americano"
              className="h-9 sm:h-12 lg:h-14 w-auto object-contain mx-auto mb-5 sm:mb-6 lg:mb-8"
              loading="eager"
            />
            <h1 className="text-[1.35rem] leading-[1.2] sm:text-3xl lg:text-4xl font-heading font-semibold text-white tracking-tight">
              Responda a pesquisa rápida e desbloqueie <span className="text-emerald-400">3 presentes exclusivos</span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-base mt-3 sm:mt-4 max-w-xl mx-auto leading-relaxed">
              Leva menos de 1 minuto. Suas respostas personalizam o conteúdo do evento e liberam o acesso ao grupo VIP com os bônus.
            </p>
          </div>

          {/* Progresso */}
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <div className="flex items-center justify-between mb-2 sm:mb-2.5 text-[11px] sm:text-sm">
              <span className="text-slate-400 font-medium">
                Etapa <span className="text-white font-bold">{stepIdx + 1}</span> de <span className="text-white font-bold">{total}</span>
              </span>
              <span className="text-emerald-400 font-bold">{progress}%</span>
            </div>
            <div className="relative h-1.5 sm:h-2 bg-white/5 rounded-full overflow-hidden border border-slate-800">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 50%, #4ade80 100%)',
                  boxShadow: '0 0 20px rgba(34,197,94,0.5)',
                }}
              />
            </div>
          </div>

          {/* Card do passo */}
          {submitting ? (
            <div className="bg-white/5 border border-slate-700/50 rounded-2xl p-8 sm:p-10 lg:p-14 text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-emerald-950/50 border border-emerald-800/50 flex items-center justify-center mx-auto mb-5 sm:mb-6">
                <Loader2 size={28} className="animate-spin text-emerald-400 sm:w-8 sm:h-8" />
              </div>
              <h3 className="text-lg sm:text-2xl lg:text-3xl font-heading font-semibold text-white mb-2 sm:mb-3">
                Preparando seu presente...
              </h3>
              <p className="text-slate-400 text-xs sm:text-base">
                Aguarde um instante enquanto liberamos seus acessos exclusivos.
              </p>
            </div>
          ) : (
            <div key={step.id} className="bg-white/5 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-8 lg:p-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <h3 className="text-base sm:text-xl lg:text-2xl font-heading font-semibold text-white leading-snug mb-5 sm:mb-7 lg:mb-8">
                {step.question}
              </h3>

              <div className="divide-y divide-slate-800">
                {step.options.map((option) => {
                  const checked = step.kind === 'radio'
                    ? currentAnswer === option
                    : Array.isArray(currentAnswer) && currentAnswer.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => step.kind === 'radio' ? selectRadio(option) : toggleCheckbox(option)}
                      className="w-full flex items-start gap-3 sm:gap-4 py-3.5 sm:py-4 px-1 text-left transition-colors duration-150 group"
                    >
                      <span
                        className={`mt-0.5 shrink-0 flex items-center justify-center w-[18px] h-[18px] sm:w-5 sm:h-5 transition-all duration-300 ease-out ${step.kind === 'radio' ? 'rounded-full' : 'rounded'
                          } ${checked
                            ? 'bg-emerald-500 border-2 border-emerald-400 text-white shadow-[0_0_12px_rgba(34,197,94,0.5)] scale-110'
                            : 'border-2 border-slate-600 group-hover:border-emerald-400 scale-100'
                          }`}
                      >
                        <span
                          className={`flex items-center justify-center transition-transform duration-300 ease-out ${checked ? 'scale-100' : 'scale-0'
                            }`}
                        >
                          {step.kind === 'radio'
                            ? <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white" />
                            : <Check size={12} strokeWidth={3} />}
                        </span>
                      </span>
                      <span className={`text-[13px] sm:text-base leading-snug sm:leading-relaxed transition-colors ${checked ? 'text-white font-medium' : 'text-slate-300 group-hover:text-white'}`}>
                        {option}
                      </span>
                    </button>
                  );
                })}
              </div>

              {step.kind === 'checkbox' && (
                <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
                  <Check size={12} /> Selecione todas as opções que se aplicam
                </p>
              )}
            </div>
          )}

          {/* Erro */}
          {!submitting && error && (
            <div
              role="alert"
              className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-flag-950/40 border border-flag-500/40 animate-in fade-in slide-in-from-top-1 duration-200"
            >
              <AlertCircle size={18} className="text-flag-300 shrink-0 mt-0.5" />
              <p className="text-flag-100 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Botões */}
          {!submitting && (
            <div className="mt-6 lg:mt-8 flex items-stretch gap-2 sm:gap-3">
              {stepIdx > 0 && (
                <button
                  onClick={handleBack}
                  className="inline-flex items-center justify-center gap-1.5 px-3 sm:px-5 py-3.5 sm:py-4 rounded-xl bg-white/5 border border-slate-700 text-slate-300 text-xs sm:text-sm font-semibold hover:bg-white/10 hover:text-white transition-all"
                  aria-label="Voltar"
                >
                  <ArrowLeft size={14} />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
              )}
              {isLast ? (
                <button
                  onClick={handleNext}
                  className="btn-cta btn-cta-sm flex-1 shadow-[0_0_30px_rgba(34,197,94,0.3)]"
                >
                  <Gift size={18} />
                  Receber presente!
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="inline-flex items-center justify-center gap-2 flex-1 px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl bg-white text-[#131314] font-bold text-sm sm:text-base hover:bg-slate-100 transition-all hover:-translate-y-0.5 active:translate-y-0"
                >
                  Próximo
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}

          {/* Privacy */}
          <p className="text-xs text-slate-500 text-center mt-8">
            Seus dados estão seguros e não serão compartilhados.
          </p>
        </div>
      </main>

      {/* Footer slim */}
      <footer className="border-t border-slate-800 py-4 px-5 sm:px-8">
        <div className="text-xs text-slate-500 text-center max-w-xl mx-auto space-y-1.5">
          <p>&copy; {new Date().getFullYear()} Giovanni Paganini · GVSI CAPITAL LTDA - 58.660.640/0001-08</p>
          <p className="text-emerald-400/80">
            Em conformidade com a LGPD · <a href="/privacidade" className="hover:text-white underline">Política de Privacidade</a>
          </p>
        </div>
      </footer>

    </div>
  );
};

export default PesquisaPage;
